import type { MailboxInfo } from 'owa-client-types';
import { trace } from 'owa-trace';

/**
 * A single entity the matcher selected for a query. Carries the matched query
 * text and the resolved person identity, so it is PII and must only be surfaced
 * through a user-consented channel (e.g. clipboard) — never logged to telemetry.
 */
export interface EntityRewriteMatchedEntity {
    readonly matchedText: string;
    /** Display name a TYPO span was corrected to; absent for EXACT and PREFIX. */
    readonly correctedText?: string;
    readonly displayName: string;
    readonly email: string;
    /** Match layer that produced this entity: EXACT | PREFIX | TYPO. */
    readonly layer: string;
    /** One-based rank of the person in the closeness-ordered directory. */
    readonly rank: number;
    /** One-based nonempty percentile closeness bucket (1 = closest). */
    readonly bucket: number;
}

/**
 * Explains why the entity-recognition rewrite did (or did not) fire for a query.
 *
 * The scalar "reason-code" fields are non-PII and safe for telemetry. The
 * `rawQuery`, `rewrittenQuery`, and `matchedEntities` fields can contain query
 * text and person identities (PII) and must only leave the device through a
 * user-consented channel.
 */
export interface EntityRewriteDiagnostics {
    // --- Non-PII reason codes (safe to log) ---
    /** Whether a people-field rewrite was produced for this query. */
    readonly rewriteApplied: boolean;
    /** Why the rewrite was skipped, when it was. */
    readonly skippedReason?: string;
    /** Number of identities in an applied rewrite; zero when rewriting was skipped. */
    readonly entityCount: number;
    /** Post-policy identities recognized before the person-only gate; absent if not analyzed. */
    readonly recognizedEntityCount?: number;
    /** Distinct recognized name spans (not identities); absent if not analyzed. */
    readonly matchedSpanCount?: number;
    /** Whitespace-delimited tokens outside recognized spans; absent if not analyzed. */
    readonly recognizedResidualKeywordCount?: number;
    /** Retained people in the account's recognizer; absent if recognition did not run. */
    readonly personDirectorySize?: number;
    /** Whitespace-delimited token count of the raw query. */
    readonly queryTokenCount: number;
    /** Stop-word (verb + filler) token count from the analysis pass. */
    readonly stopWordCount: number;
    /** Number of residual (non-entity) keyword tokens. */
    readonly residualKeywordCount: number;
    /** Match layer of the top (closest) selected entity. */
    readonly topMatchLayer?: string;
    /** Character length of the query text the top entity matched. */
    readonly topMatchedTextLength?: number;
    /**
     * Character length of the name part the top entity matched against (e.g. 6 for
     * `sumbit` matching `Sumbit Kumar`), not the full display name.
     */
    readonly topTargetNameLength?: number;
    /** One-based directory rank of the top (closest) selected entity. */
    readonly topRank?: number;
    /** One-based nonempty percentile closeness bucket of the top entity (1 = closest). */
    readonly topBucket?: number;

    // --- PII detail (clipboard / user-consented only, never logged) ---
    readonly rawQuery: string;
    readonly rewrittenQuery?: string;
    readonly matchedEntities: readonly EntityRewriteMatchedEntity[];
}

type EntityRewriteDiagnosticsProvider = (
    queryString: string,
    mailboxInfo: MailboxInfo
) => EntityRewriteDiagnostics;

let registeredProvider: EntityRewriteDiagnosticsProvider | undefined;

/**
 * Register the matcher-backed diagnostics provider. Called from the lazy
 * entity-recognition module so the heavy matcher pipeline is not pulled into the
 * eager search bundle (mirrors {@link registerEntityQueryRewriter}).
 */
export function registerEntityRewriteDiagnosticsProvider(
    provider: EntityRewriteDiagnosticsProvider | undefined
): void {
    registeredProvider = provider;
}

/**
 * Compute why the entity-recognition rewrite fired for a query, or `undefined`
 * when recognition has not been initialized for the account yet. Deterministic
 * and side-effect free.
 */
export function getEntityRewriteDiagnostics(
    queryString: string,
    mailboxInfo: MailboxInfo
): EntityRewriteDiagnostics | undefined {
    if (!registeredProvider) {
        return undefined;
    }

    try {
        return registeredProvider(queryString, mailboxInfo);
    } catch {
        trace.warn('Entity recognition diagnostics failed');
        return undefined;
    }
}
